import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  FAIR_PAGE_LOADER_MAX_MS,
  FAIR_PAGE_LOADER_MIN_MS,
  FAIR_PAGE_LOADER_SETTLE_MS,
  FAIR_PAGE_LOADER_SHOW_DELAY_MS,
} from '@/ui/tokens/fair-page-loader.data'

/**
 * Global branded page transition loader.
 * Shown for routes with `meta.pageLoader`; dismissed via `markReady()`
 * (when products/data loaded) or max timeout / navigation error.
 * Visual: monogram line-draw (stroke-dashoffset) — see FairPageLoaderMark.
 * Inline products use `motion="spin"` (short snake crawls FR contour).
 *
 * Loader A (designer spec 2026-10-19): the page's own skeleton covers the first
 * `FAIR_PAGE_LOADER_SHOW_DELAY_MS`; only a load still running then gets the overlay, and once
 * shown it stays `FAIR_PAGE_LOADER_MIN_MS` from that moment so the intro plays through.
 * `isActive` (footer hold, readiness) is true for the whole wait, overlay or not.
 */
export const useFairPageLoaderStore = defineStore('fairPageLoader', () => {
  const visible = ref(false)
  const sessionId = ref(0)
  /** When the overlay actually appeared; 0 = not shown in this session. */
  const shownAt = ref(0)
  const waitingForReady = ref(false)

  /**
   * FE-003.1 — readiness is collective, not "first caller wins". Every section that
   * owns a request registers a gate; the overlay leaves only when all of them are
   * ready, the settle grace has passed (late-mounting sections get to re-arm it) and
   * the browser has painted a frame with the real content in place.
   */
  const gates = ref<Record<number, boolean>>({})
  let nextGateId = 0

  let hideTimer: ReturnType<typeof setTimeout> | null = null
  let showTimer: ReturnType<typeof setTimeout> | null = null
  let maxTimer: ReturnType<typeof setTimeout> | null = null
  let settleTimer: ReturnType<typeof setTimeout> | null = null
  let settleFrame: number | null = null
  let settleSession = 0

  const isActive = computed(() => waitingForReady.value)

  const gatesSettled = computed(() => {
    const values = Object.values(gates.value)
    return values.length > 0 && values.every(Boolean)
  })

  function cancelSettle() {
    if (settleTimer != null) {
      clearTimeout(settleTimer)
      settleTimer = null
    }
    if (settleFrame != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(settleFrame)
      settleFrame = null
    }
    settleSession += 1
  }

  /** Two frames: the first commits the ready DOM, the second runs after it is painted. */
  function afterPaint(run: () => void) {
    if (typeof requestAnimationFrame === 'undefined') {
      run()
      return
    }
    settleFrame = requestAnimationFrame(() => {
      settleFrame = requestAnimationFrame(() => {
        settleFrame = null
        run()
      })
    })
  }

  function scheduleReady() {
    cancelSettle()
    const session = settleSession
    const routeSession = sessionId.value

    settleTimer = setTimeout(() => {
      settleTimer = null
      afterPaint(() => {
        if (session !== settleSession) return
        if (routeSession !== sessionId.value) return
        if (!waitingForReady.value || !gatesSettled.value) return
        markReady()
      })
    }, FAIR_PAGE_LOADER_SETTLE_MS)
  }

  watch(
    () => [waitingForReady.value, gatesSettled.value] as const,
    ([waiting, settled]) => {
      if (waiting && settled) scheduleReady()
      else cancelSettle()
    },
    { flush: 'post' },
  )

  function registerGate(): number {
    const id = (nextGateId += 1)
    gates.value = { ...gates.value, [id]: false }
    return id
  }

  function setGate(id: number, ready: boolean) {
    if (gates.value[id] === ready) return
    gates.value = { ...gates.value, [id]: ready }
  }

  function releaseGate(id: number) {
    if (!(id in gates.value)) return
    const next = { ...gates.value }
    delete next[id]
    gates.value = next
  }

  function clearTimers() {
    cancelSettle()
    if (hideTimer != null) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    if (showTimer != null) {
      clearTimeout(showTimer)
      showTimer = null
    }
    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = null
    }
  }

  function hide() {
    clearTimers()
    waitingForReady.value = false
    visible.value = false
  }

  function start() {
    clearTimers()
    sessionId.value += 1
    shownAt.value = 0
    waitingForReady.value = true
    visible.value = false

    const session = sessionId.value
    showTimer = setTimeout(() => {
      showTimer = null
      if (sessionId.value !== session || !waitingForReady.value) return
      shownAt.value = Date.now()
      visible.value = true
    }, FAIR_PAGE_LOADER_SHOW_DELAY_MS)

    maxTimer = setTimeout(() => {
      if (sessionId.value === session && waitingForReady.value) {
        hide()
      }
    }, FAIR_PAGE_LOADER_MAX_MS)
  }

  function markReady() {
    if (!waitingForReady.value) return

    cancelSettle()
    waitingForReady.value = false
    const session = sessionId.value

    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = null
    }

    // Settled inside the skeleton window: the overlay never appears.
    if (!visible.value) {
      if (showTimer != null) {
        clearTimeout(showTimer)
        showTimer = null
      }
      return
    }

    const remaining = Math.max(0, FAIR_PAGE_LOADER_MIN_MS - (Date.now() - shownAt.value))

    hideTimer = setTimeout(() => {
      if (sessionId.value === session) {
        visible.value = false
        hideTimer = null
      }
    }, remaining)
  }

  function fail() {
    hide()
  }

  return {
    visible,
    sessionId,
    isActive,
    gatesSettled,
    registerGate,
    setGate,
    releaseGate,
    start,
    markReady,
    fail,
    hide,
  }
})

export const useFairPageLoader = useFairPageLoaderStore
